import React, { useState, useEffect } from 'react'
import { searchPersonas, Persona } from '../api/client'

interface PersonaSearchProps {
  onSearchResults: (results: Persona[]) => void
  placeholder?: string
  className?: string
  minSearchLength?: number
}

const PersonaSearch: React.FC<PersonaSearchProps> = ({
  onSearchResults,
  placeholder = "Search advisors by name, expertise, or bio...",
  className = "",
  minSearchLength = 2
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null)

  // Handle search with debouncing
  useEffect(() => {
    if (searchQuery.trim().length < minSearchLength) {
      onSearchResults([])
      return
    }
    
    // Clear existing timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer)
    }
    
    // Set new timer for debounced search
    const timer = setTimeout(async () => {
      setIsSearching(true)
      try {
        const results = await searchPersonas(searchQuery)
        onSearchResults(results)
      } catch (err) {
        console.error('Search failed:', err)
        onSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300) // 300ms debounce
    
    setSearchDebounceTimer(timer)
    
    // Cleanup timer on unmount or query change
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [searchQuery, minSearchLength])

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        placeholder={placeholder}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full px-4 py-2 pl-10 pr-4 text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-amber-500"
      />
      <svg
        className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
      </svg>
      {isSearching && (
        <div className="absolute right-3 top-2.5">
          <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      )}
    </div>
  )
}

export default PersonaSearch